<script setup lang="ts">
import { MessageSquare, Phone, ShieldAlert } from '@lucide/vue'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useApiQuery } from '@ja/portal-query'

import en from '../locales/en.json'
import frLocale from '../locales/fr.json'

interface Coach {
    id: string
    display_name: string
    job_title: string | null
    avatar_path: string | null
    initials: string | null
    mobile: string | null
    phone: string | null
}

const TRIGGER_EVENT = 'portal-employee:open-coaches'

const { t } = useI18n({ useScope: 'isolated', messages: { en, fr: frLocale } })

const open = ref(false)
const fetchEnabled = ref(false)

const { data, isLoading } = useApiQuery<{ coaches: Coach[] }>(
    ['portal-employee', 'coaches', 'today'],
    '/api/my/coaches',
    {},
    {
        enabled: fetchEnabled,
    },
)

const coaches = computed<Coach[]>(() => data.value?.coaches ?? [])
const hasCoaches = computed(() => coaches.value.length > 0)

const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '')
    if (digits.length === 10) {
        return `${digits.slice(0, 3)} ${digits.slice(3, 6)}-${digits.slice(6)}`
    }
    if (digits.length === 11 && digits.startsWith('1')) {
        return `${digits.slice(1, 4)} ${digits.slice(4, 7)}-${digits.slice(7)}`
    }
    return value
}

const telHref = (value: string): string => `tel:${value.replace(/[^\d+]/g, '')}`
const smsHref = (value: string): string => `sms:${value.replace(/[^\d+]/g, '')}`

const handleOpen = () => {
    fetchEnabled.value = true
    open.value = true
}

onMounted(() => {
    window.addEventListener(TRIGGER_EVENT, handleOpen)
})

onBeforeUnmount(() => {
    window.removeEventListener(TRIGGER_EVENT, handleOpen)
})
</script>

<template>
    <Sheet :open="open" @update:open="(v) => (open = v)">
        <SheetContent side="right" class="flex flex-col gap-0 p-0 sm:max-w-md">
            <SheetHeader class="from-primary/15 to-primary/5 border-b bg-gradient-to-br p-6">
                <div class="flex items-center gap-3">
                    <div
                        class="bg-primary/15 text-primary flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
                    >
                        <ShieldAlert class="h-6 w-6" />
                    </div>
                    <div class="min-w-0 text-left">
                        <SheetTitle class="text-lg leading-tight">
                            {{ t('employee.coaches.banner_title') }}
                        </SheetTitle>
                        <SheetDescription class="mt-1 text-xs leading-snug">
                            {{ t('employee.coaches.banner_subtitle') }}
                        </SheetDescription>
                    </div>
                </div>
            </SheetHeader>

            <div class="flex-1 overflow-y-auto px-4 py-4">
                <div v-if="isLoading && !hasCoaches" class="text-muted-foreground py-12 text-center text-sm">
                    {{ t('employee.coaches.loading') }}
                </div>

                <ul v-else-if="hasCoaches" class="space-y-2">
                    <li
                        v-for="coach in coaches"
                        :key="coach.id"
                        class="bg-card hover:border-primary/40 flex items-center gap-3 rounded-xl border p-3 transition-colors"
                    >
                        <Avatar class="h-12 w-12 shrink-0">
                            <AvatarImage v-if="coach.avatar_path" :src="coach.avatar_path" :alt="coach.display_name" />
                            <AvatarFallback class="bg-primary/15 text-primary font-semibold">
                                {{ coach.initials ?? coach.display_name.charAt(0).toUpperCase() }}
                            </AvatarFallback>
                        </Avatar>

                        <div class="min-w-0 flex-1">
                            <p class="truncate text-sm leading-tight font-semibold">
                                {{ coach.display_name }}
                            </p>
                            <p v-if="coach.job_title" class="text-muted-foreground mt-0.5 truncate text-xs">
                                {{ coach.job_title }}
                            </p>
                            <a
                                v-if="coach.mobile || coach.phone"
                                :href="telHref((coach.mobile ?? coach.phone)!)"
                                class="text-muted-foreground hover:text-foreground mt-0.5 inline-block font-mono text-xs tabular-nums"
                            >
                                {{ formatPhone((coach.mobile ?? coach.phone)!) }}
                            </a>
                        </div>

                        <div class="flex shrink-0 items-center gap-1.5">
                            <Button
                                v-if="coach.mobile"
                                as="a"
                                :href="smsHref(coach.mobile)"
                                variant="outline"
                                size="icon"
                                class="h-10 w-10"
                                :aria-label="t('employee.coaches.text', { name: coach.display_name })"
                            >
                                <MessageSquare class="h-5 w-5" />
                            </Button>
                            <Button
                                v-if="coach.mobile || coach.phone"
                                as="a"
                                :href="telHref((coach.mobile ?? coach.phone)!)"
                                size="icon"
                                class="h-10 w-10"
                                :aria-label="t('employee.coaches.call', { name: coach.display_name })"
                            >
                                <Phone class="h-5 w-5" />
                            </Button>
                        </div>
                    </li>
                </ul>

                <div v-else class="text-muted-foreground py-12 text-center text-sm">
                    {{ t('employee.coaches.empty') }}
                </div>
            </div>
        </SheetContent>
    </Sheet>
</template>
